import { useState } from 'react';
import key from 'weak-key';
import { Container } from '@geberit/gdds';

// styles
import styles from './image-gallery.module.scss';

// types
import type { CurrentItem, ImageGalleryProps } from '../types';

// components
import { SectionClosingLine } from 'components/ContentElementsGdds/Common/section-closing-line';
import { GalleryItem } from './gallery-item';
import { Wrapper } from './wrapper';
import { MediaOverlay } from 'components/media-overlay';
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { InlineEdit } from 'components/ContentCreator/InlineEdit';

// utils
import { buildSize, gridSizes } from 'utils/gridSize';
import { useSectionId } from 'utils/hooks/use-section-id';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import { isEmpty } from 'utils/is-empty';
import { useXy } from 'utils/hooks/use-xy';
import { classNameBuilder } from 'utils/classNameBuilder';

export function GddsImageGallery({
  title,
  subtitle,
  imageGallery,
  text,
  emphasize,
  contentIndex,
  anchor,
  border,
  titleInAnchor,
  itemsPerRow = '3',
  isChild = false,
  childOf,
}: Readonly<ImageGalleryProps>) {
  const isXy = useXy();
  const sectionId = useSectionId(title, anchor ?? '', titleInAnchor, contentIndex);
  const displayClosingLine = !border;

  const [currentItem, setCurrentItem] = useState<CurrentItem | undefined>(undefined);
  const withHeadline = Boolean(title || subtitle || text);

  return (
    <>
      <section
        className={classNameBuilder(
          styles.imageGallery,
          displayClosingLine && styles['hasSCL'],
          childOf === 'accordion' && styles.marginIsAccordion,
          childOf === 'tab' && styles.marginIsTab,
        )}
        id={sectionId}
      >
        <Container
          className={classNameBuilder(childOf === 'tab' && 'tab-child-container')}
          maxContentWidth={buildSize(gridSizes.gddsFullGrid)}
        >
          <Headline
            title={title}
            subtitle={subtitle}
            text={text}
            tag={isChild && isXy ? Formats.h4 : Formats.h2}
            variant={Formats.h2}
            className={styles.headline}
            textClassName={styles.intro}
            titlePreviewId="#st_titleText"
            subtitlePreviewId="#st_subtitle"
            introPreviewId="#st_content"
            textPreviewId="#st_text"
            emphasize={emphasize}
          />

          <InlineEdit previewId="#st_imageGallery">
            {!isEmpty(imageGallery) && (
              <Wrapper
                imageGallery={imageGallery}
                itemsPerRow={itemsPerRow}
                withHeadline={withHeadline}
              >
                {imageGallery.map((item, index) => (
                  <GalleryItem
                    key={key(item)}
                    item={item}
                    setCurrentItem={setCurrentItem}
                    index={index}
                  />
                ))}
              </Wrapper>
            )}
          </InlineEdit>

          {currentItem && (
            <MediaOverlay
              items={imageGallery}
              index={currentItem.index ?? 0}
              onClose={() => setCurrentItem(undefined)}
            />
          )}
        </Container>
      </section>
      {displayClosingLine && <SectionClosingLine />}
    </>
  );
}
